<template>
  <div style="background-color:#4a5568; overflow:hidden;">
    <v-container v-if="loading" class="fill-height d-flex align-center justify-center">
      <v-progress-circular indeterminate color="#ff3b47" size="64" />
    </v-container>

    <div v-else>
      <v-container class="mb-5">
        <v-row class="text-center">
          <v-col cols="12">
            <div class="d-flex align-center justify-start mb-4">
              <v-btn icon class="back-button" @click="confirmBackNavigation"
                style="background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); border-radius: 12px; color: white;">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
            </div>
            <v-card-title style="color:white; font-size:1.5rem;" class="font-weight-bold">
              Sample Tracker
            </v-card-title>
          </v-col>
        </v-row>
      </v-container>

      <div class="pa-4" style="background-color:white; border-radius:20px 20px 0 0; min-height:70%;">
        <v-row class="d-flex justify-space-between align-center mb-4">
          <div class="text-h4 font-weight-bold mx-6 my-4"
            style="color:#2e3a59; text-transform:uppercase; letter-spacing:1px; text-shadow:2px 2px 4px rgba(0,0,0,.2);">
            SAMPLE TRACKER
          </div>
          <div class="mx-6 d-flex" style="gap: 16px;">
            <v-btn large @click="openBinsModal"
              style="background: #4a5568; color: white; border-radius: 8px; text-transform: none;"
              class="font-weight-medium">
              <v-icon left>mdi-archive</v-icon>View Bins
            </v-btn>
            <v-btn v-if="canAddSample" large @click="navigateToAddSample"
              style="background: #4a5568; color: white; border-radius: 8px; text-transform: none;"
              class="font-weight-medium">
              <v-icon left>mdi-plus</v-icon>Add Sample
            </v-btn>
          </div>
        </v-row>

        <div class="mx-6 mb-6">
          <div class="text-h6 font-weight-bold mb-3"
            style="color:#2e3a59; text-transform:uppercase; letter-spacing:0.5px;">
            Filter & Search
          </div>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field dense outlined v-model="search" prepend-inner-icon="mdi-magnify"
                label="Search Sample ID / Customer / Reference" hide-details style="border-radius: 8px;">
              </v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="selectedStatus" :items="statusOptions" dense outlined
                label="Filter by Status" prepend-inner-icon="mdi-filter" hide-details style="border-radius: 8px;">
              </v-select>
            </v-col>
          </v-row>
        </div>

        <div class="mx-6">
          <div class="text-h6 font-weight-bold mb-4"
            style="color:#2e3a59; text-transform:uppercase; letter-spacing:0.5px;">
            Samples
          </div>

          <div class="samples-grid">
            <div v-for="(item, index) in filteredItems" :key="item.id || index"
              class="sample-card" :class="{ 'clickable': canEditSample || canViewSample }"
              @click="handleSampleClick(item)">
              <div class="sample-card-header">
                <v-chip :color="getStatusColor(item.sampleStatus)" dark label small class="status-chip">
                  {{ item.sampleStatus || "Unknown" }}
                </v-chip>
                <button class="info-btn" @click.stop="openInfoModal(item)">
                  <v-icon small>mdi-information</v-icon>
                </button>
              </div>
              <div class="px-4 pb-2">
                <v-chip color="#4a5568" dark label class="sample-id-chip">
                  ID: {{ item.sampleId || 'N/A' }}
                </v-chip>
              </div>
              <div class="sample-card-content">
                <div class="info-row">
                  <span class="info-label">Customer:</span>
                  <span class="info-value">{{ item.customerName }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Reference #:</span>
                  <span class="info-value">{{ item.referenceNumber }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Received By:</span>
                  <span class="info-value">{{ item.receivedBy }}</span>
                </div>
                <div class="info-row" v-if="item.quoteNumber">
                  <span class="info-label">Quote #:</span>
                  <span class="info-value">{{ item.quoteNumber }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Date Received:</span>
                  <span class="info-value">{{ item.createdDate }}</span>
                </div>
                <div class="info-row" v-if="item.binNumber">
                  <span class="info-label">Bin:</span>
                  <span class="info-value">Bin {{ item.binNumber }}</span>
                </div>
              </div>
              <div v-if="canEditSample || canViewSample" class="sample-card-actions">
                <v-btn color="#4a5568" dark block class="action-btn"
                  style="text-transform: none; border-radius: 6px;">
                  <v-icon left small>mdi-cog</v-icon>{{ getButtonText() }}
                </v-btn>
              </div>
            </div>
          </div>

          <div v-if="filteredItems.length === 0" class="text-center mt-8">
            <div class="no-results">
              <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-test-tube</v-icon>
              <h3 style="color: #4a5568; margin-bottom: 8px;">No samples found</h3>
              <p style="color: #7f8c8d;">Try adjusting your search or filter criteria</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Info Modal -->
    <v-dialog v-model="infoDialog.visible" max-width="700">
      <v-card style="border-radius: 12px;">
        <v-card-title style="background: #4a5568; color: white; border-radius: 12px 12px 0 0;">
          <span class="modal-title">Sample ID: {{ infoDialog.sample.sampleId }} Details</span>
          <div class="header-actions">
            <v-btn icon @click="infoDialog.visible = false" style="color: white;">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text class="pa-6">
          <div class="modal-grid">
            <div class="modal-field"><div class="modal-field-label">Sample ID</div><div class="modal-field-value">{{ infoDialog.sample.sampleId || 'N/A' }}</div></div>
            <div class="modal-field"><div class="modal-field-label">Customer</div><div class="modal-field-value">{{ infoDialog.sample.customerName || 'N/A' }}</div></div>
            <div class="modal-field"><div class="modal-field-label">Reference #</div><div class="modal-field-value">{{ infoDialog.sample.referenceNumber || 'N/A' }}</div></div>
            <div class="modal-field"><div class="modal-field-label">Contact Person</div><div class="modal-field-value">{{ infoDialog.sample.contactPerson || 'N/A' }}</div></div>
            <div class="modal-field"><div class="modal-field-label">Received By</div><div class="modal-field-value">{{ infoDialog.sample.receivedBy || 'N/A' }}</div></div>
            <div class="modal-field" v-if="infoDialog.sample.assignedTo"><div class="modal-field-label">Assigned To</div><div class="modal-field-value">{{ infoDialog.sample.assignedTo || 'N/A' }}</div></div>
            <div class="modal-field" v-if="infoDialog.sample.quoteNumber"><div class="modal-field-label">Quote Number</div><div class="modal-field-value">{{ infoDialog.sample.quoteNumber || 'N/A' }}</div></div>
            <div class="modal-field"><div class="modal-field-label">Date Received</div><div class="modal-field-value">{{ infoDialog.sample.createdDate || 'N/A' }}</div></div>
            <div class="modal-field" v-if="infoDialog.sample.returnDate"><div class="modal-field-label">Date Returned</div><div class="modal-field-value">{{ infoDialog.sample.returnDate || 'N/A' }}</div></div>
            <div class="modal-field" v-if="infoDialog.sample.binNumber"><div class="modal-field-label">Bin</div><div class="modal-field-value">Bin {{ infoDialog.sample.binNumber }}</div></div>
            <div class="modal-field">
              <div class="modal-field-label">Status</div>
              <div class="modal-field-value">
                <span class="status-badge" :class="getStatusClass(infoDialog.sample.sampleStatus)">{{ infoDialog.sample.sampleStatus || 'Unknown' }}</span>
              </div>
            </div>
            <div class="modal-field"><div class="modal-field-label">Quantity</div><div class="modal-field-value">{{ infoDialog.sample.quantity || 'N/A' }}</div></div>
          </div>
          <div class="modal-field full-width mt-4" v-if="infoDialog.sample.sampleInformation">
            <div class="modal-field-label">Sample Information</div>
            <div class="modal-field-value" style="white-space: pre-wrap;">{{ infoDialog.sample.sampleInformation }}</div>
          </div>
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-btn color="#4a5568" dark block @click="copyModalContent(infoDialog.sample)" style="text-transform: none; border-radius: 8px;">
            Copy All Details
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bins Modal -->
    <SampleBinsDialog
      :visible.sync="binsDialog.visible"
      :available-bins="availableBinsList"
      :taken-bins="takenBinsList"
      :samples="items"
    />

    <!-- Back Confirmation -->
    <v-dialog v-model="backDialog" max-width="500">
      <v-card style="border-radius: 12px;">
        <v-card-title style="background: #4a5568; color: white; border-radius: 12px 12px 0 0;">Confirm Navigation</v-card-title>
        <v-card-text class="pa-6">Are you sure you want to go back?</v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="backDialog = false" style="text-transform: none;">Cancel</v-btn>
          <v-btn color="#4a5568" dark @click="goBack" style="text-transform: none; border-radius: 6px;">Yes, Go Back</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import { getFirestore, collection, getDocs, doc, getDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";
import SampleBinsDialog from "@/components/SampleBinsDialog.vue";

const db = getFirestore(firebaseApp);

const BIN_CLEAR_STATUSES = ["At Dispatch", "Return Completed"];

export default {
  name: "SamplesComponent",
  components: { SampleBinsDialog },
  data() {
    return {
      items: [],
      search: "",
      selectedStatus: "",
      statusOptions: [
        "All", "Booked", "Inspection", "Drawing", "Pending Quote",
        "Quoted", "To be Returned", "At Dispatch", "Return Completed",
      ],
      loading: true,
      backDialog: false,
      userRole: "",
      userPermissions: {},
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      infoDialog: { visible: false, sample: {} },
      binsDialog: { visible: false },
      boxes: Array.from({ length: 40 }, (_, i) => ({
        number: i + 1, taken: false, selected: false, unavailable: false,
      })),
    };
  },
  computed: {
    canAddSample() { return this.userPermissions.Add_Button === true; },
    canEditSample() { return this.userPermissions.Edit_Details === true; },
    canViewSample() { return this.userPermissions.View_Details === true; },
    filteredItems() {
      let f = this.items;
      if (this.search) {
        const s = this.search.toLowerCase();
        f = f.filter(it =>
          it.sampleId.toString().includes(s) ||
          it.customerName.toLowerCase().includes(s) ||
          (it.referenceNumber && it.referenceNumber.toLowerCase().includes(s)) ||
          (it.quoteNumber && it.quoteNumber.toLowerCase().includes(s))
        );
      }
      if (this.selectedStatus && this.selectedStatus !== "All") {
        f = f.filter(it => it.sampleStatus === this.selectedStatus);
      }
      return f
        .filter(it => !["Completed", "Rejected"].includes(it.sampleStatus))
        .sort((a, b) => b.sampleId - a.sampleId);
    },
    availableBinsList() { return this.boxes.filter(b => !b.taken && !b.unavailable); },
    takenBinsList() { return this.boxes.filter(b => b.taken); },
  },
  async created() {
    await this.fetchUserInfo();
    await this.fetchSamples();
    this.initializeBoxes();
    this.loading = false;
  },
  methods: {
    async fetchUserInfo() {
      const auth = getAuth(firebaseApp);
      await new Promise(resolve => {
        onAuthStateChanged(auth, async user => {
          if (user) {
            const snap = await getDoc(doc(db, "users", user.uid));
            if (snap.exists()) {
              const d = snap.data();
              this.userRole = d.role;
              this.userPermissions = d.samples || {};
            }
          }
          resolve();
        });
      });
    },

    async fetchSamples() {
      try {
        const snap = await getDocs(collection(db, "samples"));
        this.items = snap.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            sampleId: data.sampleId ?? "N/A",
            sampleStatus: data.sampleStatus ?? "Pending",
            customerName: data.customer?.companyName ?? "N/A",
            referenceNumber: data.referenceNumber ?? "N/A",
            contactPerson: data.contactPerson ?? "N/A",
            sampleInformation: data.sampleInformation ?? "N/A",
            returnDate: data.returnDate ?? "",
            createdDate: data.createdDate ?? "N/A",
            receivedBy: typeof data.receivedBy === 'object' && data.receivedBy !== null ? data.receivedBy.name : (data.receivedBy ?? "N/A"),
            assignedTo: typeof data.assignedTo === 'object' && data.assignedTo !== null ? data.assignedTo.name : (data.assignedTo ?? ""),
            quoteNumber: data.quoteNumber ?? "",
            binNumber: data.binNumber ?? null,
            quantity: data.sampleItems
              ? data.sampleItems.reduce((t, s) => t + Number(s.qty || 0), 0)
              : "N/A",
          };
        });
      } catch (e) {
        console.error("fetchSamples:", e);
        this.showSnackbar("Failed to fetch samples", "error");
      }
    },

    initializeBoxes() {
      this.boxes.forEach(box => { box.taken = false; box.selected = false; box.unavailable = false; });
      const takenBins = this.items
        .filter(it => it.binNumber && it.binNumber !== "N/A" && !BIN_CLEAR_STATUSES.includes(it.sampleStatus))
        .map(it => it.binNumber.toString());
      this.boxes.forEach(box => {
        if (takenBins.includes(box.number.toString())) {
          box.taken = true;
          box.unavailable = true;
        }
      });
    },

    openBinsModal() { this.binsDialog.visible = true; },

    handleSampleClick(item) {
  if (this.canEditSample) { this.$router.push({ name: "EditSample", params: { id: item.id } }); return; }
  if (this.canViewSample) { this.$router.push({ name: "ViewSample", params: { id: item.id } }); }
},

getButtonText() {
  if (this.canEditSample) return "Manage Sample";
  if (this.canViewSample) return "View Sample";
  return "View Sample";
},

    openInfoModal(item) { this.infoDialog.sample = item; this.infoDialog.visible = true; },

    getStatusColor(status) {
      const map = {
        'Booked':            '#3182ce',
        'Inspection':        '#805ad5',
        'Drawing':           '#d69e2e',
        'Pending Quote':     '#dd6b20',
        'Quoted':            '#48bb78',
        'To be Returned':    '#e53e3e',
        'At Dispatch':       '#319795',
        'Return Completed':  '#718096',
      };
      return map[status] || '#4a5568';
    },

    getStatusClass(status) {
      const map = {
        'Booked':            'status-booked',
        'Inspection':        'status-inspection',
        'Drawing':           'status-drawing',
        'Pending Quote':     'status-pending-quote',
        'Quoted':            'status-quoted',
        'To be Returned':    'status-to-be-returned',
        'At Dispatch':       'status-at-dispatch',
        'Return Completed':  'status-return-completed',
      };
      return map[status] || 'status-default';
    },

    navigateToAddSample() { this.$router.push({ name: "AddSample" }); },
    confirmBackNavigation() { this.backDialog = true; },
    goBack() { this.backDialog = false; this.$router.go(-1); },

    showSnackbar(message, color = "success") {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },

    copyModalContent(sample) {
      const content = `Sample ID: ${sample.sampleId || 'N/A'}
Customer: ${sample.customerName || 'N/A'}
Reference #: ${sample.referenceNumber || 'N/A'}
Contact Person: ${sample.contactPerson || 'N/A'}
Received By: ${sample.receivedBy || 'N/A'}
${sample.assignedTo ? `Assigned To: ${sample.assignedTo}\n` : ''}${sample.quoteNumber ? `Quote Number: ${sample.quoteNumber}\n` : ''}Date Received: ${sample.createdDate || 'N/A'}
${sample.returnDate ? `Date Returned: ${sample.returnDate}\n` : ''}${sample.binNumber ? `Bin: Bin ${sample.binNumber}\n` : ''}Status: ${sample.sampleStatus || 'Unknown'}
Quantity: ${sample.quantity || 'N/A'}
${sample.sampleInformation ? `\nSample Information:\n${sample.sampleInformation}` : ''}`;
      navigator.clipboard.writeText(content)
        .then(() => this.showSnackbar("Sample details copied successfully."))
        .catch(() => this.showSnackbar("Failed to copy sample details.", "error"));
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');
body, div, span, v-card-title, v-row, v-col, v-card { font-family: 'Montserrat', sans-serif; }
.samples-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 20px; margin-bottom: 20px; }
.sample-card { background: white; border-radius: 8px; border: 1px solid #e0e6ed; transition: all 0.3s ease; cursor: pointer; overflow: hidden; position: relative; min-height: 300px; display: flex; flex-direction: column; }
.sample-card::before { content: ''; position: absolute; top: 0; left: 0; width: 100%; height: 8px; background: #4a5568; }
.sample-card:hover { transform: translateY(-4px); box-shadow: 0 12px 30px rgba(0,0,0,0.1); border-color: #ddd; }
.sample-card-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 16px 8px 16px; margin-top: 8px; }
.info-btn { background: #f7fafc; border: 1px solid #e2e8f0; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; color: #4a5568; cursor: pointer; transition: all 0.2s ease; }
.info-btn:hover { background: #edf2f7; transform: scale(1.1); }
.sample-card-content { padding: 0 16px; flex: 1; }
.info-row { display: flex; justify-content: space-between; margin-bottom: 12px; align-items: flex-start; }
.info-label { color: #4a5568; font-weight: 600; font-size: 13px; min-width: 100px; text-transform: uppercase; letter-spacing: 0.5px; }
.info-value { color: #2d3748; font-size: 13px; text-align: right; word-break: break-word; max-width: 60%; }
.sample-card-actions { padding: 16px; margin-top: auto; }
.status-chip { font-weight: 600 !important; font-size: 11px !important; text-transform: uppercase; letter-spacing: 0.5px; }
.sample-id-chip { font-weight: 600 !important; font-size: 12px !important; letter-spacing: 0.5px; }
.status-booked           { background: rgba(49,130,206,0.2);  color: #3182ce; border: 1px solid rgba(49,130,206,0.3); }
.status-inspection       { background: rgba(128,90,213,0.2);  color: #805ad5; border: 1px solid rgba(128,90,213,0.3); }
.status-drawing          { background: rgba(214,158,46,0.2);  color: #d69e2e; border: 1px solid rgba(214,158,46,0.3); }
.status-pending-quote    { background: rgba(221,107,32,0.2);  color: #dd6b20; border: 1px solid rgba(221,107,32,0.3); }
.status-quoted           { background: rgba(72,187,120,0.2);  color: #48bb78; border: 1px solid rgba(72,187,120,0.3); }
.status-to-be-returned   { background: rgba(229,62,62,0.2);   color: #e53e3e; border: 1px solid rgba(229,62,62,0.3); }
.status-at-dispatch      { background: rgba(49,151,149,0.2);  color: #319795; border: 1px solid rgba(49,151,149,0.3); }
.status-return-completed { background: rgba(113,128,150,0.2); color: #718096; border: 1px solid rgba(113,128,150,0.3); }
.status-default { background: #718096; color: white; }
.modal-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 20px; }
.modal-field { display: flex; flex-direction: column; gap: 6px; }
.modal-field.full-width { grid-column: 1 / -1; }
.modal-field-label { color: #4a5568; font-weight: 600; font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; }
.modal-field-value { color: #2d3748; font-size: 14px; word-break: break-word; }
.status-badge { padding: 4px 12px; border-radius: 6px; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
.no-results { color: #4a5568; padding: 40px 20px; }
.modal-title { flex: 1; }
.header-actions { display: flex; gap: 8px; }
@media (max-width: 768px) {
  .samples-grid { grid-template-columns: 1fr; }
  .modal-grid { grid-template-columns: 1fr; }
  .info-row { flex-direction: column; gap: 4px; }
  .info-label, .info-value { text-align: left; max-width: 100%; }
}
</style>